import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Calendar,
  TrendingUp,
  User,
  Info,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getUserWorkoutPlansAction } from "../../../store/slices/workoutPlans/workoutPlansSliceActions";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";

const WorkoutPlansSection = ({ uid }) => {
  const [showWorkoutPlans, setShowWorkoutPlans] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState(null);

  const dispatch = useAppDispatch();
  const { workoutPlans, total, apiStatus, error, userId } = useAppSelector(
    (state) => state.workoutPlans
  );

  const isLoadingPlans = apiStatus.getUserWorkoutPlans === API_STATUS.STARTED;

  // Load workout plans when toggled on OR when uid changes
  useEffect(() => {
    if (uid && showWorkoutPlans) {
      // Always fetch if uid is different from the stored userId
      if (userId !== uid) {
        dispatch(getUserWorkoutPlansAction(uid));
      } else if (workoutPlans.length === 0) {
        // Or if no data exists for current user
        dispatch(getUserWorkoutPlansAction(uid));
      }
    }
  }, [uid, showWorkoutPlans, userId, workoutPlans.length, dispatch]);

  // Reset state when uid changes
  useEffect(() => {
    if (uid && userId && userId !== uid) {
      setExpandedPlanId(null); // Reset expanded plan when switching users
    }
  }, [uid, userId]);

  // Handle toggle
  const handleToggle = () => {
    if (!showWorkoutPlans && workoutPlans.length === 0) {
      dispatch(getUserWorkoutPlansAction(uid));
    }
    setShowWorkoutPlans(!showWorkoutPlans);
  };

  // Format timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    try {
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    } catch (err) {
      console.error("Error formatting date:", err);
      return timestamp;
    }
  };

  // Format date only (no time)
  const formatDateOnly = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (err) {
      return dateString;
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return { bg: "#dcfce7", color: "#15803d", border: "#86efac" };
      case "completed":
        return { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" };
      case "paused":
        return { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };
      default:
        return { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
    }
  };

  // Toggle plan expansion
  const togglePlanExpansion = (planId) => {
    setExpandedPlanId(expandedPlanId === planId ? null : planId);
  };

  // Format metadata value for display
  const formatMetadataValue = (value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return value;
  };

  // Render metadata in a nice card format
  const renderMetadata = (metadata) => {
    if (!metadata || typeof metadata !== "object") return null;

    const metadataConfig = {
      varietyGuarantee: { label: "Variety Guarantee", icon: "✓" },
      scienceModel: { label: "Science Model", icon: "🔬" },
      environment: { label: "Environment", icon: "🏠" },
      totalWorkouts: { label: "Total Workouts", icon: "💪" },
      equipmentAvailable: { label: "Equipment Available", icon: "🏋️" },
      generationMethod: { label: "Generation Method", icon: "⚙️" },
      workoutsPerWeek: { label: "Workouts Per Week", icon: "📅" },
      totalWeeks: { label: "Total Weeks", icon: "📆" },
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
        }}
      >
        {Object.entries(metadata).map(([key, value]) => {
          const config = metadataConfig[key] || { label: key, icon: "📊" };
          
          // Skip nested objects for now, render them separately
          if (key === "userGoals" && typeof value === "object") {
            return null;
          }

          return (
            <div
              key={key}
              style={{
                backgroundColor: "#f0f9ff",
                padding: "0.75rem",
                borderRadius: "8px",
                border: "1px solid #bae6fd",
              }}
            >
              <div
                style={{
                  fontSize: "0.7rem",
                  color: "#0369a1",
                  marginBottom: "0.25rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                <span>{config.icon}</span>
                {config.label}
              </div>
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "#0c4a6e",
                }}
              >
                {formatMetadataValue(value)}
              </div>
            </div>
          );
        })}

        {/* User Goals - render separately if exists */}
        {metadata.userGoals && typeof metadata.userGoals === "object" && (
          <div
            style={{
              gridColumn: "1 / -1",
              backgroundColor: "#fef3c7",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid #fcd34d",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: "600",
                color: "#92400e",
                marginBottom: "0.5rem",
                textTransform: "uppercase",
              }}
            >
              🎯 User Goals
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "0.5rem",
              }}
            >
              {Object.entries(metadata.userGoals).map(([goalKey, goalValue]) => (
                <div key={goalKey} style={{ fontSize: "0.8rem" }}>
                  <span style={{ color: "#92400e", fontWeight: "600" }}>
                    {goalKey.replace(/([A-Z])/g, " $1").trim()}:
                  </span>{" "}
                  <span style={{ color: "#b45309" }}>
                    {formatMetadataValue(goalValue)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render onboarding data in a nice format
  const renderOnboardingData = (onboardingData) => {
    if (!onboardingData || typeof onboardingData !== "object") return null;

    const onboardingConfig = {
      preferredIntensity: { label: "Preferred Intensity", icon: "⚡", color: "#dbeafe" },
      hasHealthConditions: { label: "Has Health Conditions", icon: "🏥", color: "#fee2e2" },
      bodyFocus: { label: "Body Focus", icon: "🎯", color: "#e0e7ff" },
      exerciseExperience: { label: "Exercise Experience", icon: "📊", color: "#fef3c7" },
      preferredTrainingDays: { label: "Training Days", icon: "📅", color: "#d1fae5" },
      workoutEnvironment: { label: "Workout Environment", icon: "🏠", color: "#f3e8ff" },
      availableEquipment: { label: "Available Equipment", icon: "🏋️", color: "#fce7f3" },
      sessionDuration: { label: "Session Duration", icon: "⏱️", color: "#ddd6fe" },
      selectedBodyType: { label: "Body Type", icon: "👤", color: "#fbcfe8" },
      workoutTypes: { label: "Workout Types", icon: "💪", color: "#ccfbf1" },
      bodyPhotoUpload: { label: "Body Photo Uploaded", icon: "📸", color: "#e0e7ff" },
    };

    return (
      <div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          {Object.entries(onboardingData).map(([key, value]) => {
            const config = onboardingConfig[key];
            
            // Skip health conditions array, render it separately
            if (key === "healthConditions") return null;
            
            if (!config) return null;

            return (
              <div
                key={key}
                style={{
                  backgroundColor: config.color,
                  padding: "0.75rem",
                  borderRadius: "8px",
                  border: `1px solid ${config.color}`,
                  filter: "brightness(0.98)",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: "#374151",
                    marginBottom: "0.25rem",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <span>{config.icon}</span>
                  {config.label}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#1f2937",
                  }}
                >
                  {formatMetadataValue(value)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Health Conditions - render separately if exists */}
        {onboardingData.healthConditions &&
          Array.isArray(onboardingData.healthConditions) && (
            <div
              style={{
                backgroundColor: "#fef2f2",
                padding: "1rem",
                borderRadius: "8px",
                border: "1px solid #fecaca",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#991b1b",
                  marginBottom: "0.5rem",
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                🏥 Health Conditions
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                {onboardingData.healthConditions.map((condition, idx) => {
                  const isSelected = condition.selected === true;
                  return (
                    <span
                      key={idx}
                      style={{
                        padding: "0.4rem 0.75rem",
                        borderRadius: "20px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        backgroundColor: isSelected ? "#dc2626" : "#f3f4f6",
                        color: isSelected ? "#fff" : "#6b7280",
                        border: isSelected ? "1px solid #b91c1c" : "1px solid #e5e7eb",
                      }}
                    >
                      {isSelected ? "✓ " : ""}
                      {condition.name}
                    </span>
                  );
                })}
              </div>
              {!onboardingData.healthConditions.some((c) => c.selected) && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#059669",
                    fontWeight: "600",
                    marginTop: "0.5rem",
                  }}
                >
                  ✓ No health conditions reported
                </div>
              )}
            </div>
          )}
      </div>
    );
  };

  // Styles
  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "0.5rem 0",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const planCardStyle = {
    backgroundColor: colors.extraLightGray,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "1rem",
    border: `1px solid ${colors.doubleExtraLightGray}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const infoGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem",
    marginTop: "1rem",
  };

  const infoItemStyle = {
    padding: "0.75rem",
    backgroundColor: colors.white,
    borderRadius: "8px",
    border: `1px solid ${colors.doubleExtraLightGray}`,
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={handleToggle}>
        <div style={titleStyle}>
          <Dumbbell size={20} />
          Workout Plans
          {total > 0 && (
            <span
              style={{
                backgroundColor: colors.darkGreen,
                color: colors.white,
                padding: "0.25rem 0.5rem",
                borderRadius: "12px",
                fontSize: "0.75rem",
                fontWeight: "600",
              }}
            >
              {total}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {showWorkoutPlans ? (
            <EyeOff size={16} color={colors.darkGray} />
          ) : (
            <Eye size={16} color={colors.darkGray} />
          )}
          {showWorkoutPlans ? (
            <ChevronUp size={20} color={colors.darkGray} />
          ) : (
            <ChevronDown size={20} color={colors.darkGray} />
          )}
          {isLoadingPlans && (
            <RefreshCw
              size={16}
              color={colors.darkGray}
              className="animate-spin"
            />
          )}
        </div>
      </div>

      {showWorkoutPlans && (
        <div style={{ marginTop: "1rem" }}>
          {/* Error Display */}
          {error && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}

          {/* Loading State */}
          {isLoadingPlans && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: colors.darkGray,
              }}
            >
              <RefreshCw
                size={32}
                style={{
                  animation: "spin 1s linear infinite",
                  marginBottom: "0.5rem",
                }}
              />
              <div>Loading workout plans...</div>
              {userId && userId !== uid && (
                <div style={{ fontSize: "0.8rem", marginTop: "0.5rem", color: colors.darkBlue }}>
                  Fetching data for new user
                </div>
              )}
            </div>
          )}

          {/* Workout Plans Display */}
          {!isLoadingPlans && workoutPlans.length > 0 ? (
            <div>
              <h4
                style={{
                  margin: "0 0 1rem 0",
                  color: colors.darkBlue,
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Found {total} workout plan{total !== 1 ? "s" : ""}
              </h4>

              {workoutPlans.map((plan) => {
                const isExpanded = expandedPlanId === plan.planId;
                const statusColors = getStatusColor(plan.status);

                return (
                  <div
                    key={plan.planId}
                    style={{
                      ...planCardStyle,
                      borderLeft: `4px solid ${statusColors.border}`,
                    }}
                    onClick={() => togglePlanExpansion(plan.planId)}
                  >
                    {/* Plan Header */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            marginBottom: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <h5
                            style={{
                              margin: 0,
                              color: colors.darkBlue,
                              fontSize: "1.1rem",
                              fontWeight: "600",
                            }}
                          >
                            Workout Plan
                          </h5>

                          {/* Status Badge */}
                          {plan.status && (
                            <span
                              style={{
                                backgroundColor: statusColors.bg,
                                color: statusColors.color,
                                padding: "0.25rem 0.75rem",
                                borderRadius: "12px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                border: `1px solid ${statusColors.border}`,
                              }}
                            >
                              {plan.status}
                            </span>
                          )}

                          {/* Language Badge */}
                          {plan.workoutPlanLanguage && (
                            <span
                              style={{
                                backgroundColor: "#e0e7ff",
                                color: "#3730a3",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "12px",
                                fontSize: "0.7rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              {plan.workoutPlanLanguage}
                            </span>
                          )}

                          {/* Current Week Badge */}
                          {plan.currentWeek !== undefined && (
                            <span
                              style={{
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "12px",
                                fontSize: "0.7rem",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                              }}
                            >
                              <TrendingUp size={12} />
                              Week {plan.currentWeek}
                            </span>
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: "0.875rem",
                            color: colors.darkGray,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <Calendar size={14} />
                          Created: {formatDate(plan.createdAt)}
                        </div>

                        {/* Date Range */}
                        {(plan.startDate || plan.endDate) && (
                          <div
                            style={{
                              fontSize: "0.875rem",
                              color: colors.darkGray,
                              marginTop: "0.5rem",
                            }}
                          >
                            {plan.startDate && (
                              <span>
                                Start: {formatDateOnly(plan.startDate)}
                              </span>
                            )}
                            {plan.startDate && plan.endDate && (
                              <span style={{ margin: "0 0.5rem" }}>→</span>
                            )}
                            {plan.endDate && (
                              <span>End: {formatDateOnly(plan.endDate)}</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        {isExpanded ? (
                          <ChevronUp size={20} color={colors.darkGray} />
                        ) : (
                          <ChevronDown size={20} color={colors.darkGray} />
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div
                        style={{ marginTop: "1.5rem" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Body Type Detection */}
                        {plan.detectedBodyType && (
                          <div style={{ marginBottom: "1rem" }}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                color: colors.darkGray,
                                marginBottom: "0.5rem",
                                textTransform: "uppercase",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                              }}
                            >
                              <User size={14} />
                              Detected Body Type
                            </div>
                            <div style={infoGridStyle}>
                              {plan.detectedBodyType.bodyType && (
                                <div style={infoItemStyle}>
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      color: colors.darkGray,
                                      marginBottom: "0.25rem",
                                    }}
                                  >
                                    Body Type
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      color: colors.darkBlue,
                                    }}
                                  >
                                    {plan.detectedBodyType.bodyType}
                                  </div>
                                </div>
                              )}
                              {plan.detectedBodyType.muscleTone && (
                                <div style={infoItemStyle}>
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      color: colors.darkGray,
                                      marginBottom: "0.25rem",
                                    }}
                                  >
                                    Muscle Tone
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      color: colors.darkBlue,
                                    }}
                                  >
                                    {plan.detectedBodyType.muscleTone}
                                  </div>
                                </div>
                              )}
                              {plan.detectedBodyType.bodyFat && (
                                <div style={infoItemStyle}>
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      color: colors.darkGray,
                                      marginBottom: "0.25rem",
                                    }}
                                  >
                                    Body Fat
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      color: colors.darkBlue,
                                    }}
                                  >
                                    {plan.detectedBodyType.bodyFat}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Body Photos */}
                        {plan.bodyPhotos && plan.bodyPhotos.length > 0 && (
                          <div style={{ marginBottom: "1rem" }}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                color: colors.darkGray,
                                marginBottom: "0.5rem",
                                textTransform: "uppercase",
                              }}
                            >
                              Body Photos ({plan.bodyPhotos.length})
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                flexWrap: "wrap",
                              }}
                            >
                              {plan.bodyPhotos.map((photo, idx) => (
                                <img
                                  key={idx}
                                  src={photo.url || photo}
                                  alt={`Progress ${idx + 1}`}
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: `2px solid ${colors.lightGray}`,
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Workout Plan Details */}
                        {plan.workoutPlan && (
                          <div style={{ marginBottom: "1rem" }}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                color: colors.darkGray,
                                marginBottom: "0.5rem",
                                textTransform: "uppercase",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                              }}
                            >
                              <Info size={14} />
                              Plan Details
                            </div>

                            {/* Why This Plan */}
                            {plan.workoutPlan.whyThisPlan && (
                              <div
                                style={{
                                  backgroundColor: colors.white,
                                  padding: "1rem",
                                  borderRadius: "8px",
                                  marginBottom: "1rem",
                                  border: `1px solid ${colors.doubleExtraLightGray}`,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.7rem",
                                    fontWeight: "600",
                                    color: colors.darkGray,
                                    marginBottom: "0.5rem",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Why This Plan
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.875rem",
                                    color: colors.darkBlue,
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {plan.workoutPlan.whyThisPlan}
                                </div>
                              </div>
                            )}

                            {/* Plan Overview */}
                            {plan.workoutPlan.planOverview && (
                              <div
                                style={{
                                  backgroundColor: colors.white,
                                  padding: "1rem",
                                  borderRadius: "8px",
                                  marginBottom: "1rem",
                                  border: `1px solid ${colors.doubleExtraLightGray}`,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.7rem",
                                    fontWeight: "600",
                                    color: colors.darkGray,
                                    marginBottom: "0.5rem",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Plan Overview
                                </div>
                                <div style={{ fontSize: "0.875rem" }}>
                                  {Object.entries(
                                    plan.workoutPlan.planOverview
                                  ).map(([key, value]) => (
                                    <div
                                      key={key}
                                      style={{
                                        marginBottom: "0.5rem",
                                        display: "flex",
                                        gap: "0.5rem",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: "600",
                                          color: colors.darkGray,
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {key.replace(/([A-Z])/g, " $1")}:
                                      </span>
                                      <span style={{ color: colors.darkBlue }}>
                                        {typeof value === "object"
                                          ? JSON.stringify(value)
                                          : value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Weeks */}
                            {plan.workoutPlan.weeks &&
                              Array.isArray(plan.workoutPlan.weeks) && (
                                <div
                                  style={{
                                    backgroundColor: colors.white,
                                    padding: "1rem",
                                    borderRadius: "8px",
                                    marginBottom: "1rem",
                                    border: `1px solid ${colors.doubleExtraLightGray}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      fontWeight: "600",
                                      color: colors.darkGray,
                                      marginBottom: "0.5rem",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Workout Schedule (
                                    {plan.workoutPlan.weeks.length} weeks)
                                  </div>
                                  <div
                                    style={{
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {plan.workoutPlan.weeks.map(
                                      (week, wIdx) => (
                                        <div
                                          key={wIdx}
                                          style={{
                                            marginBottom: "0.75rem",
                                            padding: "0.75rem",
                                            backgroundColor:
                                              colors.extraLightGray,
                                            borderRadius: "6px",
                                            border:
                                              plan.currentWeek === wIdx + 1
                                                ? `2px solid ${colors.darkGreen}`
                                                : `1px solid ${colors.doubleExtraLightGray}`,
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontWeight: "600",
                                              color: colors.darkBlue,
                                              marginBottom: "0.25rem",
                                            }}
                                          >
                                            Week {wIdx + 1}
                                            {plan.currentWeek === wIdx + 1 && (
                                              <span
                                                style={{
                                                  marginLeft: "0.5rem",
                                                  backgroundColor:
                                                    colors.darkGreen,
                                                  color: colors.white,
                                                  padding: "0.1rem 0.5rem",
                                                  borderRadius: "10px",
                                                  fontSize: "0.65rem",
                                                }}
                                              >
                                                CURRENT
                                              </span>
                                            )}
                                          </div>
                                          {typeof week === "object" && (
                                            <div
                                              style={{
                                                fontSize: "0.8rem",
                                                color: colors.darkGray,
                                              }}
                                            >
                                              {Object.entries(week)
                                                .slice(0, 3)
                                                .map(([k, v]) => (
                                                  <div key={k}>
                                                    <strong>
                                                      {k
                                                        .replace(
                                                          /([A-Z])/g,
                                                          " $1"
                                                        )
                                                        .trim()}
                                                      :
                                                    </strong>{" "}
                                                    {typeof v === "object"
                                                      ? JSON.stringify(
                                                          v
                                                        ).substring(0, 50) +
                                                        "..."
                                                      : String(v).substring(
                                                          0,
                                                          100
                                                        )}
                                                  </div>
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Safety Info */}
                            {plan.workoutPlan.safetyInfo && (
                              <div
                                style={{
                                  backgroundColor: "#fef3c7",
                                  padding: "1rem",
                                  borderRadius: "8px",
                                  border: "1px solid #fcd34d",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.7rem",
                                    fontWeight: "600",
                                    color: "#92400e",
                                    marginBottom: "0.5rem",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  ⚠️ Safety Information
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#92400e",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {typeof plan.workoutPlan.safetyInfo ===
                                  "object"
                                    ? JSON.stringify(
                                        plan.workoutPlan.safetyInfo
                                      )
                                    : plan.workoutPlan.safetyInfo}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Metadata */}
                        {plan.workoutPlanMetadata && (
                          <div style={{ marginBottom: "1rem" }}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                color: colors.darkGray,
                                marginBottom: "0.75rem",
                                textTransform: "uppercase",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                              }}
                            >
                              <Info size={14} />
                              Workout Plan Metadata
                            </div>
                            {renderMetadata(plan.workoutPlanMetadata)}
                          </div>
                        )}

                        {/* Onboarding Data */}
                        {plan.onboardingData && (
                          <div>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                color: colors.darkGray,
                                marginBottom: "0.75rem",
                                textTransform: "uppercase",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                              }}
                            >
                              <User size={14} />
                              User Onboarding Data
                            </div>
                            {renderOnboardingData(plan.onboardingData)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            !isLoadingPlans && (
              <div
                style={{
                  padding: "3rem",
                  textAlign: "center",
                  color: colors.darkGray,
                }}
              >
                No workout plans found for this user.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default WorkoutPlansSection;
